import { createContext, useContext } from 'react';

/**
 * PD-16033, generalized in PD-16220 — full-screen layers and where the user
 * was when they opened one.
 *
 * Settings and the Application Guide each render as a blocking layer over
 * the page the user came from. The app keeps that page mounted underneath by
 * rendering the main route tree against the last location outside every
 * layer (see AppRoutes in crm-web), and renders the layer's own routes on
 * top with the real location. Closing a layer navigates back to that page.
 * A deep link straight into a layer has no origin, so the layer opens over
 * the dashboard and Close returns there.
 */
export interface LayerOriginModel {
	/** pathname + search + hash of the page underneath, or null for a deep link. */
	originPath: string | null;
}

/**
 * The address prefixes that open as a layer instead of a page. Adding one
 * here is all AppRoutes needs; the module's routes under the prefix render in
 * the layer's own route tree.
 */
export const LAYER_PREFIXES: ReadonlyArray<string> = ['/settings', '/readme'];

/**
 * The origin of the layer on screen. AppRoutes gives each layer its own
 * provider around that layer's routes. Every layer closes to the same page,
 * the one under all layers: a layer opened from another layer (the command
 * palette can open Settings from the guide) closes to the page beneath both,
 * so two layers can never send the user back and forth between each other.
 */
export const LayerOriginContext = createContext<LayerOriginModel>({
	originPath: null,
});

export const LAYER_ORIGIN_FALLBACK_PATH = '/dashboard';

export function useLayerOrigin(): LayerOriginModel & {
	closePath: string;
} {
	const value = useContext(LayerOriginContext);
	return {
		...value,
		closePath: value.originPath ?? LAYER_ORIGIN_FALLBACK_PATH,
	};
}

function isUnderPrefix(pathname: string, prefix: string): boolean {
	return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** The layer prefix an address belongs to, or null for an ordinary page. */
export function findLayerPrefix(pathname: string): string | null {
	return LAYER_PREFIXES.find((prefix) => isUnderPrefix(pathname, prefix)) ?? null;
}

export function isLayerPath(pathname: string): boolean {
	return findLayerPrefix(pathname) != null;
}

export function isSettingsPath(pathname: string): boolean {
	return isUnderPrefix(pathname, '/settings');
}
